import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle, Minus } from "lucide-react";
import { motion } from "framer-motion";

interface InsightCardProps {
  title: string;
  value: string | number;
  unit?: string;
  trend?: "up" | "down" | "stable";
  status?: "critical" | "warning" | "normal" | "good";
  description?: string;
  index: number;
  onClick?: () => void;
}

export function InsightCard({ title, value, unit, trend, status = "normal", description, index, onClick }: InsightCardProps) {
  const statusColors = {
    critical: "from-red-500 to-red-600",
    warning: "from-amber-500 to-orange-600",
    normal: "from-gray-500 to-gray-600",
    good: "from-emerald-500 to-green-600"
  };

  const statusIcons = {
    critical: AlertCircle,
    warning: AlertCircle,
    normal: Minus,
    good: CheckCircle
  };

  const StatusIcon = statusIcons[status];
  const trendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const TrendIcon = trendIcon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Card 
        className={`relative overflow-hidden hover:shadow-lg transition-all duration-300 border-l-4 border-l-primary ${
          onClick ? "cursor-pointer hover:scale-[1.02]" : ""
        }`}
        onClick={onClick}
      >
        <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${statusColors[status]} opacity-10 rounded-bl-full`}></div>
        
        <CardContent className="pt-6 pb-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-foreground">{value}</span>
                {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
              </div>
            </div>
            <div className={`p-2 rounded-lg bg-gradient-to-br ${statusColors[status]}`}>
              <StatusIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          
          {description && (
            <p className="text-xs text-muted-foreground mb-2">{description}</p>
          )}
          
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendIcon className={`h-4 w-4 ${trend === "up" ? "text-emerald-600" : trend === "down" ? "text-red-600" : "text-gray-500"}`} />
              <span className="text-xs font-medium text-muted-foreground">
                {trend === "up" ? "Tendência crescente" : trend === "down" ? "Tendência decrescente" : "Estável"}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
